import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router';

// Constants
import PageUrls from 'Shared/constants/PageUrls.constants';

// Hooks
import { useUpdateAiSetting } from 'Shared/utils/hooks/use-update-ai-setting';
import { useUpdateAiReport } from 'Shared/utils/hooks/use-update-ai-report';
import { useUpdateAiSocket } from 'Shared/utils/hooks/use-update-ai-socket';
import { useToggle } from 'Shared/utils/hooks';

// Components
import { WpPageLayout, WpPageHeader, WpPageBody, WpPageTitle } from 'Wpds/components/page-layout';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpBadge } from 'Wpds/components/badge';
import WpIcon from 'Wpds/components/icon/WpIcon';
import SettingsRail from './SettingsRail';
import TemplateSections from './TemplateSections/TemplateSections';
import ReportSectionsList from './ReportSections/ReportSectionsList';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';
import { WpModal, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import WpAlert from 'Wpds/components/alert/WpAlert';
import InstructionsTitle from './partials/InstructionsTitle';

// Styles
import './UpdateAiSettingPage.scss';

const UpdateAiSettingPage = () => {
  const history = useHistory();
  const { slug } = useParams();

  const [errorMessage, setErrorMessage] = useState(null);
  const [startDate, setStartDate] = useState<string>(null);
  const [endDate, setEndDate] = useState<string>(null);
  const [isBeingCancelled, toggleIsBeingCancelled] = useToggle(false);

  const {
    isLoaded,
    report,
    sections,
    hasSlackConfigured,
    hasJiraConfigured,
    hasAsanaConfigured,
    hasHubspotConfigured,
    hasSalesforceConfigured,
  } = useUpdateAiSetting({
    slug,
    withFetch: true,
  });
  const {
    isGenerating,
    hasGeneratedSections,
    handlePublishReport,
    handleGenerateReport,
    handleCancelGenerateReport,
  } = useUpdateAiReport({
    slug: report?.slug,
  });

  const hasSections = sections.length > 0;

  useUpdateAiSocket({ reportSlug: report?.slug });

  const handleUpdateDates = ({ start, end }: { start: string; end: string }) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleGenerateReportClick = ({
    templateSectionSlugs = sections.map(s => s.slug),
  }: {
    templateSectionSlugs?: string[];
  }) => {
    if (
      hasSlackConfigured ||
      hasJiraConfigured ||
      hasAsanaConfigured ||
      hasHubspotConfigured ||
      hasSalesforceConfigured
    ) {
      handleGenerateReport({ startDate, endDate, templateSectionSlugs });
    } else {
      setErrorMessage('Please configure at least one integration to generate the update from.');
    }
  };

  const handleCancelReportClick = () => {
    toggleIsBeingCancelled();
    handleCancelGenerateReport({ templateSectionSlugs: sections.map(s => s.slug) });
  };

  const handlePublishClick = () => {
    if (!report.name) {
      setErrorMessage('Please add a report name before you publish.');
    } else {
      handlePublishReport();
    }
  };

  const handleBackClick = () => {
    history.push(PageUrls.UPDATE_AI_PAGE);
  };

  useEffect(() => {
    if (isBeingCancelled && !isGenerating) {
      toggleIsBeingCancelled();
    }
  }, [isGenerating, isBeingCancelled]);

  if (!report) return null;
  const baseClassName = 'UpdateAiSettingPage';

  return (
    <>
      <WpPageLayout className={`${baseClassName}__main`} width="full">
        <WpPageHeader sticky slim>
          <WpPageTitle
            title="Status Update"
            postfix={
              <WpBadge color="purple" variant="bold">
                Beta
              </WpBadge>
            }
            buttonsContainer={
              <WpButtonGroup>
                {hasGeneratedSections && (
                  <WpButton onClick={handlePublishClick} disabled={isGenerating}>
                    Publish
                  </WpButton>
                )}
                <WpButton variant="tertiary" onClick={handleBackClick}>
                  <WpIcon name="times" />
                </WpButton>
              </WpButtonGroup>
            }
          />
        </WpPageHeader>
        {!isLoaded ? (
          <WpPageBody>loading...</WpPageBody>
        ) : (
          <div className={`${baseClassName}__content`}>
            <div className={`${baseClassName}__content__rail`}>
              <SettingsRail updateAiSettingSlug={slug} handleUpdateDates={handleUpdateDates} />
            </div>
            <WpPageBody className={`${baseClassName}__content__body`} width="full">
              <div className={`${baseClassName}__content__body__template-sections`}>
                <InstructionsTitle
                  title="Outline & Section Prompts"
                  description="UpdateAI will curate the content to the outline and prompts you specify below."
                />
                <TemplateSections updateAiSettingSlug={slug} handleRegerateSection={handleGenerateReportClick} />
              </div>
              <div className={`${baseClassName}__content__body__reports`}>
                {hasGeneratedSections || isGenerating ? (
                  <div>
                    <InstructionsTitle
                      title="Update Editor"
                      description="Edit the text below like a word doc. Format and customize as needed."
                    />
                    {isGenerating || isBeingCancelled ? (
                      <div className={`${baseClassName}__content__body__reports__toolbar`}>
                        <WpButton
                          onClick={handleCancelReportClick}
                          variant="secondary"
                          block
                          loading={isBeingCancelled}
                        >
                          <WpIcon name="times" />
                          Cancel Generating Update
                        </WpButton>
                      </div>
                    ) : (
                      <div className={`${baseClassName}__content__body__reports__toolbar`}>
                        <WpButton onClick={handleGenerateReportClick} variant="secondary" block>
                          <WpIcon name="redo" />
                          Regenerate Update
                        </WpButton>
                      </div>
                    )}
                    <ReportSectionsList
                      updateAiSettingSlug={slug}
                      handleGenerateReportClick={handleGenerateReportClick}
                    />
                  </div>
                ) : (
                  <div className={`${baseClassName}__content__body__reports__empty`}>
                    <EmptyStateContainer
                      subTitle="Get started by adding inputs and specifying an outline on the left then Generate Update"
                      button={
                        <WpButton size="lg" onClick={handleGenerateReportClick} disabled={!hasSections}>
                          <WpIcon name="redo" />
                          Generate Update
                        </WpButton>
                      }
                    />
                  </div>
                )}
              </div>
            </WpPageBody>
          </div>
        )}
      </WpPageLayout>
      {errorMessage !== null && (
        <WpModal isOpen={errorMessage !== null} onToggle={() => setErrorMessage(null)} size="sm">
          <WpModalBody>
            <WpAlert type="danger">{errorMessage}</WpAlert>
          </WpModalBody>
          <WpModalFooter>
            <WpButton variant="tertiary" onClick={() => setErrorMessage(null)}>
              Ok
            </WpButton>
          </WpModalFooter>
        </WpModal>
      )}
    </>
  );
};

export default UpdateAiSettingPage;
